import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import { useLazyQuery, useMutation } from '@apollo/client'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { AgGridReact } from 'ag-grid-react'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-quartz.css'
import { MdArrowBack } from 'react-icons/md'
import { useTranslation } from 'react-i18next'
import { FaTimes } from 'react-icons/fa'
import {
  Add,
  FileUploadOutlined,
  ReplayOutlined,
  Visibility
} from '@mui/icons-material'
import AppButton from '../../shared/AppButton'
import CustomFilterInput from '../../shared/CustomFilterInput'
import AppModal from '../../components/AppModal'
import Pagination from '../../components/Pagination'
import AppTable from '../../shared/AppTable'
import TableSkeletonLoader from '../../common/TableLoading'
import { API_BASE_URL } from '../../config/apiConfig'
import claims from '../../gql/claims'
import BulkReplaceTabs from './components/BulkReplaceTabs'
import ClaimsBulkSidebarLayout from './components/ClaimsBulkSidebarLayout'

ModuleRegistry.registerModules([AllCommunityModule])

const DEFAULT_GRID_ROWS = 100

const emptyReplacement = () => ({
  old_icd_code_id: '',
  new_icd_code_id: '',
  new_description: ''
})

const createGridRows = () =>
  Array.from({ length: DEFAULT_GRID_ROWS }, (_, index) => ({
    ...emptyReplacement(),
    row_no: index + 1
  }))

const defaultDateRange = () => {
  const toDateInput = date => date.toISOString().slice(0, 10)
  const toDate = new Date()
  const fromDate = new Date()
  fromDate.setMonth(fromDate.getMonth() - 1)
  return { fromDate: toDateInput(fromDate), toDate: toDateInput(toDate) }
}

const getMessage = error =>
  error?.graphQLErrors?.[0]?.message ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Something went wrong. Please try again.'

const formatDate = value => (value ? new Date(value).toLocaleString() : '—')

function SummaryCard({ label, value }) {
  return (
    <div className='rounded-md border border-slate-200 bg-slate-50 px-3 py-2'>
      <p className='text-[10px] font-medium uppercase tracking-wide text-slate-500'>
        {label}
      </p>
      <p className='mt-1 text-base font-semibold text-slate-800'>
        {value ?? 0}
      </p>
    </div>
  )
}

export default function ClaimDiagnosisBulkReplace() {
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'
  const navigate = useNavigate()
  const location = useLocation()
  const gridRef = useRef(null)
  const isReplacePage = location.pathname.endsWith('/replace')
  const defaultDates = useMemo(defaultDateRange, [])
  const [inputMode, setInputMode] = useState('manual')
  const [fromDate, setFromDate] = useState(defaultDates.fromDate)
  const [toDate, setToDate] = useState(defaultDates.toDate)
  const [replacements, setReplacements] = useState(createGridRows)
  const [file, setFile] = useState(null)
  const [result, setResult] = useState(null)
  const [logsPage, setLogsPage] = useState(1)
  const [confirmRevert, setConfirmRevert] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [downloadingTemplate, setDownloadingTemplate] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [runReplace, { loading: replacing }] = useMutation(
    claims.BULK_REPLACE_CLAIM_DIAGNOSES
  )
  const [runRevert, { loading: reverting }] = useMutation(
    claims.REVERT_CLAIM_DIAGNOSIS_BULK_REPLACE
  )
  const [
    loadLogs,
    { data: logsData, loading: loadingLogs, refetch: refetchLogs }
  ] = useLazyQuery(claims.LIST_CLAIM_DIAGNOSIS_BULK_LOGS, {
    fetchPolicy: 'network-only',
    onError: error => toast.error(getMessage(error))
  })

  const logs = logsData?.listClaimDiagnosisBulkLogs?.data || []
  const logPages = logsData?.listClaimDiagnosisBulkLogs?.pages || 1

  const resultStats = useMemo(
    () => [
      ['Claims scanned', result?.total_claim_requests_scanned],
      ['Claims updated', result?.total_claims_updated],
      ['Diagnoses matched', result?.total_diagnoses_matched],
      ['Diagnoses updated', result?.total_diagnoses_updated],
      ['Skipped', result?.skipped_count]
    ],
    [result]
  )

  const mappingColumns = useMemo(
    () => [
      {
        headerName: '#',
        field: 'row_no',
        width: 48,
        minWidth: 48,
        maxWidth: 56,
        editable: false,
        filter: false,
        cellClass: 'text-center text-xs text-gray-500'
      },
      {
        headerName: 'Old ICD Code',
        field: 'old_icd_code_id',
        editable: true,
        flex: 1,
        minWidth: 180,
        cellEditorParams: { maxLength: 50 }
      },
      {
        headerName: 'New ICD Code',
        field: 'new_icd_code_id',
        editable: true,
        flex: 1,
        minWidth: 180,
        cellEditorParams: { maxLength: 50 }
      },
      {
        headerName: 'New Description',
        field: 'new_description',
        editable: true,
        flex: 2,
        minWidth: 280,
        cellEditorParams: { maxLength: 500 }
      },
      {
        headerName: 'Actions',
        width: 85,
        minWidth: 85,
        maxWidth: 85,
        editable: false,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: params => (
          <button
            type='button'
            onClick={() =>
              setReplacements(rows =>
                rows.length > 1
                  ? rows
                    .filter((_, index) => index !== params.node.rowIndex)
                    .map((row, index) => ({ ...row, row_no: index + 1 }))
                  : rows
              )
            }
            disabled={replacements.length === 1}
            className='inline-flex h-5 w-5 items-center justify-center rounded text-red-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:text-slate-300'
            title='Remove'
          >
            <FaTimes size={11} />
          </button>
        )
      }
    ],
    [replacements.length]
  )

  const defaultColDef = useMemo(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      editable: true,
      minWidth: 96,
      width: 112
    }),
    []
  )

  const logColumns = useMemo(
    () => [
      {
        header: 'Actions',
        align: 'center',
        render: job => (
          <div
            className='grid gap-x-2 w-max'
            style={{ gridTemplateColumns: 'repeat(2, max-content)' }}
          >
            <button
              type='button'
              title='View details'
              onClick={() =>
                navigate('/claims/diagnosis-bulk-replace/job/' + job.job_id)
              }
              className='text-gray-500 hover:text-blue-600'
            >
              <Visibility sx={{ fontSize: 19 }} />
            </button>
            {/* <button
              type='button'
              title='Revert'
              onClick={() => setConfirmRevert(job)}
              disabled={job.status === 'reverted'}
              className='text-red-500 hover:text-red-700 disabled:text-slate-300 disabled:cursor-not-allowed'
            >
              <ReplayOutlined sx={{ fontSize: 19 }} />
            </button> */}
          </div>
        )
      },
      // { header: 'Job ID', field: 'job_id' },
      { header: 'Type', field: 'job_type' },
      {
        header: 'Status',
        render: job => (
          <span className='rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600'>
            {job.status || '—'}
          </span>
        )
      },
      {
        header: 'Date Range',
        render: job => `${job.from_date || '—'} — ${job.to_date || '—'}`
      },
      {
        header: 'Mappings',
        field: 'replacement_count',
        align: 'center'
      },
      {
        header: 'Claims Updated',
        field: 'total_claims_updated',
        align: 'center'
      },
      {
        header: 'Diagnoses Updated',
        field: 'total_diagnoses_updated',
        align: 'center'
      },
      { header: 'Created At', render: job => formatDate(job.created_at) }
    ],
    [navigate]
  )

  const fetchLogs = useCallback(
    (page = 1) => {
      setLogsPage(page)
      return loadLogs({ variables: { input: { page, limit: 10 } } })
    },
    [loadLogs]
  )

  useEffect(() => {
    if (!isReplacePage) fetchLogs()
  }, [isReplacePage, fetchLogs])

  const validateCommon = () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both request dates.')
      return false
    }
    if (fromDate > toDate) {
      toast.error('From Date cannot be after To Date.')
      return false
    }
    return true
  }

  const saveResult = data => {
    if (!data?.job_id)
      throw new Error('The bulk replacement did not return a job ID.')
    setResult(data)
    toast.success('Diagnosis replacement completed successfully.')
    if (refetchLogs) refetchLogs()
  }
  const submitManual = async () => {
    if (!validateCommon()) return

    const gridApi = gridRef.current?.api
    if (gridApi) gridApi.stopEditing()

    const currentRows = []
    gridApi?.forEachNode(node => currentRows.push(node.data))

    const dataFields = ['old_icd_code_id', 'new_icd_code_id', 'new_description']

    const populatedRows = currentRows.filter(row =>
      dataFields.some(field => String(row[field] || '').trim())
    )

    if (!populatedRows.length) {
      toast.error('Enter at least one mapping row.')
      return
    }

    const hasIncompleteRow = populatedRows.some(row =>
      dataFields.some(field => !String(row[field] || '').trim())
    )
    if (hasIncompleteRow) {
      toast.error(
        'Each filled row must have all three: Old ICD Code, New ICD Code, and New Description.'
      )
      return
    }
    const codes = populatedRows.map(row =>
      row.old_icd_code_id.trim().toLowerCase()
    )
    if (new Set(codes).size !== codes.length) {
      toast.error('Each old ICD code can only be replaced once.')
      return
    }

    try {
      const response = await runReplace({
        variables: {
          input: {
            from_date: fromDate,
            to_date: toDate,
            replacements: populatedRows.map(row => ({
              old_icd_code_id: row.old_icd_code_id.trim(),
              new_icd_code_id: row.new_icd_code_id.trim(),
              new_description: row.new_description.trim()
            }))
          }
        }
      })
      const payload = response.data?.bulkReplaceClaimDiagnoses
      if (String(payload?.status) !== '200') throw new Error(payload?.message)
      saveResult(payload.data)
    } catch (error) {
      toast.error(getMessage(error))
    }
  }

  const submitExcel = async () => {
    if (!validateCommon()) return
    if (!file) {
      toast.error('Choose an Excel file before submitting.')
      return
    }
    if (!/\.xlsx?$/i.test(file.name)) {
      toast.error('Please upload an .xlsx or .xls file.')
      return
    }
    const token = localStorage.getItem('token')
    if (!token) {
      toast.error('Your session has expired. Please log in again.')
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    formData.append('from_date', fromDate)
    formData.append('to_date', toDate)
    try {
      setUploading(true)
      const response = await axios.post(
        `${API_BASE_URL}/claim/diagnosis/bulk-replace`,
        formData,
        { headers: { Authorization: `Bearer ${token.replace(/"/g, '')}` } }
      )
      const payload = response.data?.data || response.data
      if (String(response.data?.status || '200') !== '200') {
        throw new Error(response.data?.message || response.data?.error)
      }
      saveResult(payload)
      setFile(null)
    } catch (error) {
      toast.error(getMessage(error))
    } finally {
      setUploading(false)
    }
  }

  const downloadTemplate = async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      toast.error('Your session has expired. Please log in again.')
      return
    }
    try {
      setDownloadingTemplate(true)
      const response = await axios.get(
        `${API_BASE_URL}/claim/diagnosis/bulk-replace/template`,
        {
          headers: { Authorization: `Bearer ${token.replace(/"/g, '')}` },
          responseType: 'blob'
        }
      )
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'diagnosis_bulk_replace_template.xlsx')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast.error(getMessage(error))
    } finally {
      setDownloadingTemplate(false)
    }
  }

  const handleFileDrop = e => {
    e.preventDefault()
    setDragOver(false)
    const droppedFile = e.dataTransfer?.files?.[0]
    if (droppedFile && /\.xlsx?$/i.test(droppedFile.name)) {
      setFile(droppedFile)
    } else if (droppedFile) {
      toast.error('Please upload an .xlsx or .xls file.')
    }
  }

  const revertJob = async () => {
    if (!confirmRevert) return
    try {
      const response = await runRevert({
        variables: {
          input: {
            job_id: confirmRevert.job_id,
            batch_size: Number(confirmRevert.batch_size) || 200
          }
        }
      })
      const payload = response.data?.revertClaimDiagnosisBulkReplace
      if (String(payload?.status) !== '200') throw new Error(payload?.message)
      toast.success(
        payload?.message || 'The diagnosis changes have been reverted.'
      )
      setConfirmRevert(null)
      fetchLogs()
    } catch (error) {
      toast.error(getMessage(error))
    }
  }

  const updateGridRow = event => {
    const { colDef, newValue, rowIndex } = event
    setReplacements(rows =>
      rows.map((row, index) =>
        index === rowIndex ? { ...row, [colDef.field]: newValue || '' } : row
      )
    )
  }

  const handleCellFocused = params => {
    const column = params.api.getColumn(params.column)
    const colDef = column?.getColDef()

    if (!colDef?.editable || params.rowIndex == null) {
      return
    }

    setTimeout(() => {
      if (params.api.getEditingCells().length > 0) return
      params.api.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: column.getColId()
      })
    }, 50)
  }

  const handleCellClicked = params => {
    const column = params.api.getColumn(params.column)
    const colDef = column?.getColDef()

    if (params.rowIndex == null || !colDef?.editable) {
      return
    }

    if (params.api.getEditingCells().length > 0) return

    setTimeout(() => {
      params.api.startEditingCell({
        rowIndex: params.rowIndex,
        colKey: column.getColId()
      })
    }, 50)
  }

  const handleGridReady = params => {
    params.api.setFocusedCell(0, 'old_icd_code_id')
    params.api.startEditingCell({
      rowIndex: 0,
      colKey: 'old_icd_code_id'
    })
  }

  return (
    <ClaimsBulkSidebarLayout>
      <div className='w-full'>
        <div className='mb-2 flex flex-col justify-between gap-2 sm:flex-row sm:items-center'>
          <div className='flex items-center gap-2'>
            {isReplacePage ? (
              <div
                onClick={() => navigate('/claims/diagnosis-bulk-replace')}
                className='bg-[#0070C2] hover:bg-[#0070C2]/80 cursor-pointer rounded-full p-1'
                title='Back to diagnosis replacement logs'
              >
                <MdArrowBack
                  className={
                    isRTL
                      ? 'text-white h-4 w-4 rotate-180'
                      : 'text-white h-4 w-4'
                  }
                />
              </div>
            ) : null}
            <div>
              <h1 className='text-lg font-semibold text-black/70'>
                {isReplacePage
                  ? 'Replace Diagnosis Codes'
                  : ''}
              </h1>
              <p className='text-[11px] text-slate-500'>
                {isReplacePage
                  ? 'Replace ICD diagnosis codes for claim requests within a date range.'
                  : ''}
              </p>
            </div>
          </div>
          {!isReplacePage && (
            <AppButton
              label='Replace Codes'
              startIcon={<Add />}
              onClick={() => navigate('/claims/diagnosis-bulk-replace/replace')}
            />
          )}
          {isReplacePage && (
            <div className='flex items-center gap-3'>
              <div className='flex gap-1 rounded-md border border-slate-200 p-0.5'>
                {['manual', 'excel'].map(mode => (
                  <button
                    key={mode}
                    type='button'
                    onClick={() => setInputMode(mode)}
                    className={`rounded px-3 py-1.5 text-xs font-medium ${inputMode === mode
                      ? 'bg-[#0070C2] text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                      }`}
                  >
                    {mode === 'excel'
                      ? 'Update ICD by Excel'
                      : 'Manual Replace'}
                  </button>
                ))}
              </div>
              {inputMode === 'excel' && (
                <button
                  type='button'
                  onClick={downloadTemplate}
                  disabled={downloadingTemplate}
                  className='rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-wait'
                >
                  {downloadingTemplate ? 'Downloading...' : 'Download Template'}
                </button>
              )}
            </div>
          )}
        </div>
        <div className='w-full'>
          {isReplacePage && (
            <div>
              <div className='flex flex-wrap items-center gap-3 bg-white px-4 py-2.5 rounded-md border border-slate-200 mb-2'>
                <CustomFilterInput
                  label={
                    <span>
                      {t('From Date')} <span className='text-red-500'>*</span>
                    </span>
                  }
                  type='date'
                  name='fromDate'
                  id='fromDate'
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  placeholder={t('From Date')}
                  className='w-full sm:w-[180px]'
                />
                <CustomFilterInput
                  label={
                    <span>
                      {t('To Date')} <span className='text-red-500'>*</span>
                    </span>
                  }
                  type='date'
                  name='toDate'
                  id='toDate'
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  placeholder={t('To Date')}
                  className='w-full sm:w-[180px]'
                />
              </div>

              {/* <p className='mt-1 text-[11px] text-slate-500'>
                Date range filters claim requests by{' '}
                <span className='font-medium text-slate-600'>created_at</span>.
              </p> */}

              {inputMode === 'manual' ? (
                <div className='mt-2'>
                  <div className='ag-theme-quartz diagnosis-bulk-replace-grid h-[calc(100vh-270px)] w-full overflow-hidden rounded-md border border-slate-200'>
                    <AgGridReact
                      ref={gridRef}
                      columnDefs={mappingColumns}
                      rowData={replacements}
                      defaultColDef={defaultColDef}
                      enableRtl={isRTL}
                      animateRows
                      stopEditingWhenCellsLoseFocus
                      undoRedoCellEditing
                      undoRedoCellEditingLimit={50}
                      suppressRowClickSelection
                      onCellValueChanged={updateGridRow}
                      onCellFocused={handleCellFocused}
                      onCellClicked={handleCellClicked}
                      onGridReady={handleGridReady}
                    />
                  </div>
                  <div className='mt-1 flex justify-end'>
                    <button
                      type='button'
                      className='flex items-center gap-1 text-xs text-[#0070C2] underline disabled:text-slate-400'
                      onClick={() =>
                        setReplacements(rows => [
                          ...rows,
                          { ...emptyReplacement(), row_no: rows.length + 1 }
                        ])
                      }
                      disabled={replacing}
                    >
                      <Add fontSize='small' /> Add New Row
                    </button>
                  </div>
                </div>
              ) : (
                <div className='mt-2'>
                  <div
                    onDrop={handleFileDrop}
                    onDragOver={e => {
                      e.preventDefault()
                      setDragOver(true)
                    }}
                    onDragLeave={() => setDragOver(false)}
                    className={`relative rounded-lg border-2 border-dashed px-4 py-6 flex flex-col items-center justify-center text-center transition-colors cursor-pointer ${dragOver
                      ? 'border-[#0070C2] bg-blue-50'
                      : file
                        ? 'border-[#0070C2] bg-blue-50'
                        : 'border-slate-300 bg-slate-50 hover:border-[#0070C2] hover:bg-blue-50/40'
                      }`}
                  >
                    {file ? (
                      <>
                        <button
                          type='button'
                          onClick={e => {
                            e.stopPropagation()
                            setFile(null)
                          }}
                          className='absolute top-2 right-2 text-slate-400 hover:text-red-500'
                        >
                          <FaTimes size={16} />
                        </button>
                        <FileUploadOutlined className='text-3xl mb-2 text-[#0070C2]' />
                        <p className='text-xs font-semibold text-slate-700 truncate max-w-full px-8'>
                          {file.name}
                        </p>
                      </>
                    ) : (
                      <>
                        <FileUploadOutlined
                          className={`text-3xl mb-2 ${dragOver ? 'text-[#0070C2]' : 'text-slate-400'
                            }`}
                        />
                        <p className='text-xs font-semibold text-slate-600'>
                          Drag and drop your Excel file here
                        </p>
                        <p className='text-[10px] text-slate-400 mt-0.5'>
                          Supported: .xlsx, .xls
                        </p>
                        <input
                          type='file'
                          hidden
                          id='excel-upload'
                          accept='.xls,.xlsx'
                          onChange={e => {
                            setFile(e.target.files?.[0] || null)
                            setDragOver(false)
                          }}
                        />
                        <label htmlFor='excel-upload' className='mt-3'>
                          <AppButton
                            label='Browse files'
                            variant='outlined'
                            bgColor='transparent'
                            onClick={() =>
                              document.getElementById('excel-upload').click()
                            }
                          />
                        </label>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className='mt-2 flex justify-end gap-2 border-t border-slate-200 pt-2'>
                <AppButton
                  label='Reset'
                  variant='outlined'
                  bgColor='transparent'
                  onClick={() => {
                    setFromDate(defaultDates.fromDate)
                    setToDate(defaultDates.toDate)
                    setReplacements(createGridRows())
                    setFile(null)
                    setResult(null)
                  }}
                  disabled={replacing || uploading}
                />
                <AppButton
                  label={
                    inputMode === 'manual' ? 'Replace All' : 'Upload ICD Excel'
                  }
                  startIcon={
                    inputMode === 'manual' ? (
                      <ReplayOutlined />
                    ) : (
                      <FileUploadOutlined />
                    )
                  }
                  onClick={inputMode === 'manual' ? submitManual : submitExcel}
                  isLoading={replacing || uploading}
                  loadingText='Processing...'
                />
              </div>

              {result && (
                <div className='mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2'>
                  <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
                    <div>
                      <p className='text-xs font-semibold text-emerald-800'>
                        Bulk replacement completed
                      </p>
                      <p className='mt-0.5 text-[11px] text-emerald-700'>
                        Job ID: {result.job_id} · Request date field:{' '}
                        {result.request_date_field || 'created_at'}
                      </p>
                    </div>
                    <AppButton
                      label='View job details'
                      variant='outlined'
                      bgColor='transparent'
                      onClick={() =>
                        navigate(
                          '/claims/diagnosis-bulk-replace/job/' + result.job_id
                        )
                      }
                    />
                  </div>
                  <div className='grid grid-cols-2 gap-2 md:grid-cols-5'>
                    {resultStats.map(([label, value]) => (
                      <SummaryCard key={label} label={label} value={value} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!isReplacePage && (
            <div className='pt-0'>
              <AppTable
                columns={logColumns}
                data={logs}
                loading={loadingLogs}
                actions={false}
                bodyRowPadding='7px'
                LoaderComponent={
                  <TableSkeletonLoader columnCount={9} rowCount={8} />
                }
              />
              <div className='mt-2'>
                <Pagination
                  currentPage={logsPage}
                  totalPages={logPages}
                  onPageChange={page => fetchLogs(page)}
                />
              </div>
            </div>
          )}
        </div>

        <AppModal
          open={Boolean(confirmRevert)}
          onClose={() => !reverting && setConfirmRevert(null)}
          title='Revert diagnosis replacement'
          maxWidth='sm'
        >
          <p className='text-sm text-slate-600'>
            This will revert all diagnosis changes made by this bulk replace
            job. This action affects the claims updated by job{' '}
            <span className='font-mono text-xs'>{confirmRevert?.job_id}</span>.
          </p>
          <div className='mt-5 flex justify-end gap-2'>
            <AppButton
              label='Cancel'
              variant='outlined'
              bgColor='transparent'
              onClick={() => setConfirmRevert(null)}
              disabled={reverting}
            />
            <AppButton
              label='Revert changes'
              bgColor='#dc2626'
              onClick={revertJob}
              isLoading={reverting}
              loadingText='Reverting...'
            />
          </div>
        </AppModal>

        <style>
          {`
          .diagnosis-bulk-replace-grid {
            --ag-border-color: #9ca3af;
            --ag-row-border-color: #cbd5e1;
            --ag-header-background-color: #e5e7eb;
            --ag-header-foreground-color: #111827;
            --ag-odd-row-background-color: #f8fafc;
            --ag-cell-horizontal-border: solid #cbd5e1;
            --ag-row-height: 30px;
            --ag-header-height: 32px;
            --ag-font-size: 12px;
          }

          .diagnosis-bulk-replace-grid .ag-root-wrapper,
          .diagnosis-bulk-replace-grid .ag-header,
          .diagnosis-bulk-replace-grid .ag-cell {
            border-color: #cbd5e1;
          }

          .diagnosis-bulk-replace-grid .ag-header-cell,
          .diagnosis-bulk-replace-grid .ag-header-group-cell {
            border-right: 1px solid #9ca3af;
            font-weight: 700;
          }

          .diagnosis-bulk-replace-grid .ag-cell {
            border-right: 1px solid #cbd5e1;
            border-bottom: 1px solid #e2e8f0;
            padding-left: 6px;
            padding-right: 6px;
          }

          .diagnosis-bulk-replace-grid .ag-cell-focus {
            outline: none;
          }

          .diagnosis-bulk-replace-grid .ag-cell-inline-editing {
            padding: 0 !important;
            outline: 2px solid #0070c2;
            outline-offset: -2px;
          }

          .diagnosis-bulk-replace-grid .ag-cell-edit-wrapper,
          .diagnosis-bulk-replace-grid .ag-cell-editor,
          .diagnosis-bulk-replace-grid .ag-text-field,
          .diagnosis-bulk-replace-grid .ag-wrapper,
          .diagnosis-bulk-replace-grid .ag-input-wrapper,
          .diagnosis-bulk-replace-grid input.ag-input-field-input {
            width: 100% !important;
            height: 100% !important;
            min-width: 0 !important;
            box-sizing: border-box;
          }

          .diagnosis-bulk-replace-grid input.ag-input-field-input {
            padding: 0 6px !important;
          }
        `}
        </style>
      </div>
    </ClaimsBulkSidebarLayout>
  )
}
